import React, { useState } from 'react';
import { Dumbbell, BarChart3, BrainCircuit, ClipboardList, Sun, Moon, Users } from 'lucide-react';
import { CalculatorIcon, ArrowRightIcon, ZapIcon, ShieldIcon } from './icons';
import { useThemeStore } from '../store/useThemeStore';

interface Props {
    onLogin: () => void;
    onRegister: () => void;
}

export default function LandingPage({ onLogin, onRegister }: Props) {
    const { dark, toggle } = useThemeStore();

    return (
        <main className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors overflow-x-hidden">
            {/* ── Top Nav ── */}
            <nav className="sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
                    <div className="flex items-center gap-2 select-none">
                        <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
                            <Dumbbell size={18} className="text-white" />
                        </div>
                        <span className="font-bold text-lg tracking-tight">
                            APEX<span className="text-brand-600 dark:text-brand-500 ml-0.5">Builder</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={toggle}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                            aria-label="Toggle theme"
                        >
                            {dark ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                        <button
                            onClick={onLogin}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                        >
                            Zaloguj się
                        </button>
                        <button
                            onClick={onRegister}
                            className="px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shadow-md shadow-brand-600/20"
                        >
                            Zarejestruj się
                        </button>
                    </div>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="relative py-20 sm:py-32 overflow-hidden">
                {/* Gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-600/10 via-transparent to-purple-600/10 dark:from-brand-600/5 dark:to-purple-600/5" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-500/10 dark:bg-brand-500/5 rounded-full blur-3xl" />

                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-bold mb-6">
                        <ZapIcon size={12} /> 100% darmowy projekt studencki
                    </div>

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-6">
                        Twórz profesjonalne
                        <br />
                        <span className="bg-gradient-to-r from-brand-500 to-purple-500 bg-clip-text text-transparent">
                            plany treningowe
                        </span>
                    </h1>

                    <p className="text-lg sm:text-xl text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
                        Zaawansowany kreator planów treningowych z analizą objętości, asystentem AI,
                        ankietami dla klientów i kalkulatorami fitness.
                        Stworzony z myślą o trenerach personalnych i pasjonatach siłowni.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            onClick={onRegister}
                            className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-base transition-colors shadow-lg shadow-brand-600/25 flex items-center justify-center gap-2"
                        >
                            Zacznij za darmo <ArrowRightIcon size={18} />
                        </button>
                        <button
                            onClick={onLogin}
                            className="w-full sm:w-auto px-8 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:border-brand-400 transition bg-white dark:bg-slate-900"
                        >
                            Mam już konto
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Features ── */}
            <section className="py-16 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
                        Wszystko czego potrzebujesz
                    </h2>
                    <p className="text-center text-slate-500 dark:text-slate-400 mb-12 max-w-xl mx-auto">
                        Kompletne narzędzie do planowania treningu od tworzenia planu po analizę postępów.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <FeatureCard
                            icon={<Dumbbell size={24} className="text-brand-500" />}
                            title="Kreator planów"
                            description="Buduj programy treningowe z periodyzacją, deloadami i pełną kontrolą nad objętością, intensywnością i doborem ćwiczeń."
                            color="brand"
                        />
                        <FeatureCard
                            icon={<BrainCircuit size={24} className="text-purple-500" />}
                            title="Asystent AI"
                            description="Inteligentna analiza planu: wykrywa braki, dysbalanse, ryzyko przetrenowania i sugeruje ulepszenia."
                            color="purple"
                        />
                        <FeatureCard
                            icon={<BarChart3 size={24} className="text-blue-500" />}
                            title="Analityka objętości"
                            description="Śledzenie serii na grupę mięśniową, MEV/MRV/MAV, rozkład intensywności i indeks zmęczenia."
                            color="blue"
                        />
                        <FeatureCard
                            icon={<ClipboardList size={24} className="text-amber-500" />}
                            title="Ankiety dla klientów"
                            description="Twórz ankiety, wysyłaj link klientom i automatycznie analizuj odpowiedzi dotyczące kontuzji, celów i sprzętu."
                            color="amber"
                        />
                        <FeatureCard
                            icon={<CalculatorIcon size={24} className="text-green-500" />}
                            title="Kalkulatory fitness"
                            description="BMI, zapotrzebowanie kaloryczne (TDEE), maksymalny ciężar (1RM), makroskładniki i wynik Wilksa."
                            color="green"
                        />
                        <FeatureCard
                            icon={<ShieldIcon size={24} className="text-red-500" />}
                            title="Baza 270+ ćwiczeń"
                            description="Obszerna baza ćwiczeń z polskimi nazwami, wzorcami ruchu, grupami mięśniowymi i domyślnymi parametrami."
                            color="red"
                        />
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="py-16 sm:py-20">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <div className="bg-gradient-to-br from-brand-600 to-purple-600 rounded-2xl px-8 py-12 sm:py-16 text-white">
                        <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                            Gotowy na lepsze treningi?
                        </h2>
                        <p className="text-brand-100 mb-8 max-w-lg mx-auto">
                            Dołącz do APEXBuilder i zacznij tworzyć profesjonalne plany treningowe, całkowicie za darmo.
                        </p>
                        <button
                            onClick={onRegister}
                            className="px-8 py-3.5 rounded-xl bg-white text-brand-700 font-bold text-base hover:bg-brand-50 transition-colors shadow-lg flex items-center gap-2 mx-auto"
                        >
                            Stwórz konto <ArrowRightIcon size={18} />
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="border-t border-slate-200 dark:border-slate-800 py-8">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                        <Dumbbell size={14} className="text-brand-500" />
                        <span>APEXBuilder | Projekt studencki © {new Date().getFullYear()}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-4 sm:mt-0">
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 dark:hover:text-slate-200 transition">
                            Regulamin
                        </a>
                        <span className="text-slate-300 dark:text-slate-700 hidden sm:inline">|</span>
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 dark:hover:text-slate-200 transition">
                            Polityka prywatności
                        </a>
                    </div>
                </div>
            </footer>
        </main>
    );
}

// ── Feature Card ──
function FeatureCard({
    icon,
    title,
    description,
    color,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    color: string;
}) {
    return (
        <div className="group p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl hover:border-brand-400 dark:hover:border-brand-500 hover:shadow-xl transition-all">
            <div className={`w-12 h-12 rounded-xl bg-${color}-100 dark:bg-${color}-950 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                {icon}
            </div>
            <h3 className="font-bold text-base mb-2">{title}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                {description}
            </p>
        </div>
    );
}
